import { useEffect, useRef, useState, type ReactNode } from 'react';
import { message } from 'antd';
import { useInView } from 'motion/react';
import { Button } from '@admin-panel/ui';
import type {
  LookDTOType,
  LookGroupDTOType,
  LookGroupLookDTOType,
} from '../../../../schemas/styling';
import {
  useUnpublishLookMutation,
} from '../../../../store/stylist/stylistApi';
import LookEditorPane from './LookEditorPane';
import LookWorkspaceMedia from './LookWorkspaceMedia';
import { emptyFunction } from '@admin-panel/utils';

type LookGroupWorkspaceRowProps = {
  group: LookGroupDTOType;
  look: LookGroupLookDTOType;
  workflowLine: string;
  onLookUpdated: (look: LookDTOType) => void;
  publishControl?: ReactNode | null;
  eagerLoad?: boolean;
  mediaStickyTopClassName?: string;
};

const LookGroupWorkspaceRow = ({
  group,
  look,
  workflowLine,
  onLookUpdated,
  publishControl,
  eagerLoad = false,
  mediaStickyTopClassName = 'top-(--look-group-row-top)',
}: LookGroupWorkspaceRowProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const isInView = useInView(ref, {
    margin: '320px 0px 320px 0px',
    amount: 0.15,
  });
  const [shouldLoad, setShouldLoad] = useState(eagerLoad);
  const [unpublishLook, { isLoading: isUnPublishing }] = useUnpublishLookMutation();
  const isPublished = look.qc_status === 'published';
  const publishMessageKey = `look-group-row-publish-${look.id}`;

  useEffect(() => {
    if (eagerLoad || isInView) {
      setShouldLoad(true);
    }
  }, [eagerLoad, isInView]);

  const handleUnpublish = async () => {
    try {
      message.loading({
        key: publishMessageKey,
        content: 'Unpublishing look...',
        duration: 0,
      });

      const updatedLook = await unpublishLook({ id: look.id }).unwrap();

      onLookUpdated(updatedLook);
      message.success({
        key: publishMessageKey,
        content: 'Look unpublished',
      });
    } catch (error) {
      console.error('Error unpublishing look:', error);
      message.error({
        key: publishMessageKey,
        content: 'Failed to unpublish look',
      });
    }
  };

  const defaultPublishControl = (
    <div className="flex items-center gap-2">
      <Button
                type="button"
                size="medium"
                variant="outline"
                isLoading={isUnPublishing}
                isDisabled={!isPublished}
                onClick={handleUnpublish}
              >
                Unpublish
      </Button>
    </div>
  );

  return (
    <div ref={ref} className="grid items-start grid-cols-[22rem_minmax(0,1fr)] gap-5">
      <div className={`sticky z-10 ${mediaStickyTopClassName}`}>
        <LookWorkspaceMedia
          group={group}
          look={look}
          workflowLine={workflowLine}
          publishControl={publishControl === undefined ? defaultPublishControl : publishControl}
        />
      </div>

      <LookEditorPane
        lookId={look.id}
        onClose={emptyFunction}
        onLookUpdated={onLookUpdated}
        showCloseControls={false}
        showPublishControls={false}
        embedded
        enabled={shouldLoad}
      />
    </div>
  );
};

export default LookGroupWorkspaceRow;
